import { computeBaseOverall, nextForm, ratingResistance } from './balance.js';

/**
 * Results-driven ratings.
 *
 * The attribute sliders are a human's opening guess. This layer corrects that
 * guess against what actually happened: if the balancer said two sides were
 * even and one of them won 6-1, somebody in that game is rated wrong, and the
 * scoreline is better evidence than the sliders.
 *
 * A player's effective rating is therefore three things stacked:
 *
 *   base    the attribute average — a deliberate human assessment, never edited
 *           automatically
 *   offset  what results have taught us — persistent, accumulates slowly
 *   form    a recent hot/cold streak — decays back toward zero every week
 *
 * Offset and form are deliberately separate. Form is supposed to fade; a
 * correction to someone's underlying rating is not.
 */

/** How far one game can move a rating offset. */
export const K = 0.18;

/** Goals of unexpected margin that count as a maximum surprise. */
export const MARGIN_SCALE = 4;

/** Rating points of team advantage that predict one goal of margin. */
export const PREDICT_SCALE = 0.9;

/** Ceiling on how far results may drag a player from their attribute rating. */
export const OFFSET_LIMIT = 2;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 5);

/** The margin this rating gap says we should have seen. */
export function expectedMargin(strengthA, strengthB) {
  return (strengthA - strengthB) * PREDICT_SCALE;
}

/**
 * How wrong the prediction was, as a number in [-1, 1]. Positive means Team A
 * outperformed what their ratings implied.
 */
export function surpriseOf({ strengthA, strengthB, scoreA, scoreB }) {
  const actual = (scoreA ?? 0) - (scoreB ?? 0);
  return clamp((actual - expectedMargin(strengthA, strengthB)) / MARGIN_SCALE, -1, 1);
}

/**
 * Replays every completed game in order and returns each member's learned
 * offset and current form.
 *
 * Deriving these by replay rather than mutating them in place is what makes
 * editing an old result possible. Incremental updates can't be undone — form
 * decays, so by week five there is no arithmetic that recovers what week two
 * should have been. Replaying from scratch is always correct, and for a friend
 * group's few hundred games it costs nothing.
 */
export function replayRatings(members, games) {
  const base = {};
  const state = {};
  members.forEach((m) => {
    base[m.id] = computeBaseOverall(m.attributes);
    state[m.id] = { offset: 0, form: 0, played: 0, history: [] };
  });

  const ratingOf = (id) =>
    clamp((base[id] ?? 5) + (state[id]?.offset ?? 0) + (state[id]?.form ?? 0), 1, 10);

  [...games]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .forEach((game) => {
      const known = (game.participants || []).filter((p) => state[p.memberId]);
      const teamA = known.filter((p) => p.team === 'A');
      const teamB = known.filter((p) => p.team === 'B');
      if (!teamA.length || !teamB.length) return;

      const strengthA = mean(teamA.map((p) => ratingOf(p.memberId)));
      const strengthB = mean(teamB.map((p) => ratingOf(p.memberId)));
      const surprise = surpriseOf({ strengthA, strengthB, scoreA: game.score_a, scoreB: game.score_b });

      const diff = (game.score_a ?? 0) - (game.score_b ?? 0);
      const next = {};

      known.forEach((p) => {
        const s = state[p.memberId];
        const onTeamA = p.team === 'A';

        // Results half: rated too low if your side beat the prediction.
        const raw = (onTeamA ? 1 : -1) * surprise * K;
        const { gain, loss } = ratingResistance(ratingOf(p.memberId));
        const scaled = raw >= 0 ? raw * gain : raw * loss;

        // Opinion half: the existing performance-driven streak.
        const outcome = diff === 0 ? 0 : onTeamA === diff > 0 ? 1 : -1;

        const offset = clamp(s.offset + scaled, -OFFSET_LIMIT, OFFSET_LIMIT);
        const form = nextForm({
          currentForm: s.form,
          baseRating: (base[p.memberId] ?? 5) + s.offset,
          performance: p.performance,
          goals: p.goals,
          assists: p.assists,
          outcome,
        });

        next[p.memberId] = {
          offset,
          form,
          played: s.played + 1,
          history: [
            ...s.history,
            {
              gameId: game.id,
              at: game.created_at,
              rating: clamp((base[p.memberId] ?? 5) + offset + form, 1, 10),
              outcome,
            },
          ],
        };
      });

      // Applied together, so everyone in a game is judged against the ratings
      // that stood when it kicked off.
      Object.assign(state, next);
    });

  Object.keys(state).forEach((id) => {
    state[id].offset = Number(state[id].offset.toFixed(3));
  });
  return state;
}

/**
 * How often the balancer's prediction matched the actual winner.
 * The only honest test of whether any of this is working.
 */
export function balanceAccuracy(members, games) {
  const base = {};
  members.forEach((m) => (base[m.id] = computeBaseOverall(m.attributes)));

  let decisive = 0;
  let correct = 0;
  let draws = 0;

  games.forEach((game) => {
    const known = (game.participants || []).filter((p) => base[p.memberId] != null);
    const teamA = known.filter((p) => p.team === 'A');
    const teamB = known.filter((p) => p.team === 'B');
    if (!teamA.length || !teamB.length) return;

    const diff = (game.score_a ?? 0) - (game.score_b ?? 0);
    if (diff === 0) {
      draws += 1;
      return;
    }
    const strengthA = mean(teamA.map((p) => base[p.memberId]));
    const strengthB = mean(teamB.map((p) => base[p.memberId]));
    if (Math.abs(strengthA - strengthB) < 0.001) return;

    decisive += 1;
    if (strengthA > strengthB === diff > 0) correct += 1;
  });

  return { decisive, correct, draws, rate: decisive ? correct / decisive : null };
}
